import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, BookmarkSimple, Trash, Warning } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { FIT_BADGE } from "@/lib/fit";
import { track, itemFromProduct } from "@/lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Bookmarks = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [ttlDays, setTtlDays] = useState(30);
  const [categoryById, setCategoryById] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/bookmarks`, { withCredentials: true });
        if (cancelled) return;
        setItems(res.data?.items || []);
        setTtlDays(res.data?.ttl_days || 30);
        // Categories drive the size label per product (shoes → UK, clothing → ft).
        try {
          const cats = await axios.get(`${API}/categories`);
          if (!cancelled) {
            setCategoryById(
              (cats.data || []).reduce((acc, c) => {
                acc[c.id] = c;
                return acc;
              }, {})
            );
          }
        } catch (_) {
          /* non-fatal: falls back to "ft" */
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          toast.error("Please sign in to see your bookmarks");
          navigate("/login");
          return;
        }
        console.error("Error fetching bookmarks:", err);
        toast.error("Could not load your bookmarks");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleRemove = async (product) => {
    // Optimistic removal; restore on failure.
    const prev = items;
    setItems((list) => list.filter((p) => p.id !== product.id));
    try {
      await axios.post(
        `${API}/products/${product.id}/bookmark`,
        {},
        { withCredentials: true }
      );
      track("remove_from_wishlist", {
        items: [itemFromProduct(product)],
        source: "bookmarks_page",
      });
      toast.success("Removed from bookmarks");
    } catch (err) {
      console.error("Remove bookmark failed:", err);
      setItems(prev);
      toast.error("Could not remove bookmark");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <Toaster position="top-center" richColors />
      <header className="border-b border-[#E4E1D9] px-6 md:px-12 py-4 flex items-center gap-4">
        {/* Bookmarks is reached from the Profile module now, so Back goes there. */}
        <button
          type="button"
          onClick={() => navigate("/profile")}
          className="flex items-center gap-2 text-[#6B6A68] hover:text-[#1A1A1A] transition-colors text-xs uppercase tracking-[0.2em]"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="bookmarks-back"
        >
          <ArrowLeft size={16} /> Profile
        </button>
        <h1
          className="text-2xl md:text-3xl tracking-tighter ml-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          North of Six Club
        </h1>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 md:py-14">
        <p
          className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#6B6A68] mb-2"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
        >
          Saved
        </p>
        <h2
          className="text-4xl md:text-5xl tracking-tighter text-[#2D302A] mb-6 md:mb-8"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          Your bookmarks
        </h2>

        {/* 30-day auto-removal warning — always shown at the top. */}
        <div
          className="flex items-start gap-3 bg-[#FBF3E2] border border-[#E9D9B0] text-[#7A5B12] rounded-md px-4 py-3 mb-10"
          data-testid="bookmarks-warning"
        >
          <Warning size={20} weight="fill" className="shrink-0 mt-0.5 text-[#C9A227]" />
          <p className="text-sm leading-relaxed">
            Heads up - bookmarks are automatically removed {ttlDays} days after you
            save them. Grab anything you still want before its countdown runs out.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 md:gap-x-8 gap-y-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="w-full aspect-[1/2] bg-[#E4E1D9] rounded-md mb-3" />
                <div className="h-4 bg-[#E4E1D9] rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <BookmarkSimple size={40} weight="regular" className="mx-auto text-[#C9C5BA] mb-4" />
            <p className="text-[#6B6A68] mb-6">You haven't bookmarked anything yet.</p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-xs uppercase tracking-[0.2em] text-[#2D302A] hover:underline"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              Browse products →
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 md:gap-x-8 gap-y-8 md:gap-y-12">
            {items.map((product) => (
              <div key={product.id} className="group" data-testid="bookmark-item">
                <div className="relative overflow-hidden bg-white border border-[#E4E1D9] mb-3">
                  {product.image_url && (
                    <a
                      href={product.product_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        // Saved items link straight out to the retailer, so this
                        // is the same conversion the detail page records —
                        // tagged with its own source so the two stay separable.
                        track("outbound_click", {
                          link_url: product.product_link,
                          outbound: true,
                          source: "bookmarks_page",
                          items: [itemFromProduct(product)],
                          product_name: product.product_name,
                          product_category: product.category,
                          product_link: product.product_link,
                          event_category: "Product Engagement",
                          event_label: product.product_name,
                        })
                      }
                    >
                      <img
                        src={`${BACKEND_URL}${product.image_url}`}
                        alt={product.product_name}
                        loading="lazy"
                        className="w-full aspect-[1/2] object-cover hover:scale-[1.02] transition-transform duration-500 ease-out"
                      />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemove(product)}
                    className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm hover:bg-white p-2 rounded-full shadow-lg transition-all hover:scale-110"
                    title="Remove bookmark"
                    aria-label="Remove bookmark"
                    data-testid="bookmark-remove"
                  >
                    <Trash size={16} weight="bold" className="text-red-600" />
                  </button>
                  {/* Days remaining before auto-removal */}
                  <span
                    className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.12em] bg-black/70 text-white px-2 py-1 rounded-sm"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    data-testid="bookmark-days-left"
                  >
                    {product.days_left <= 1
                      ? "Removed soon"
                      : `${product.days_left} days left`}
                  </span>
                </div>

                <div className="space-y-1.5">
                  <h3
                    className="text-base md:text-lg tracking-tight line-clamp-2"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {product.product_name}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {product.heights && product.heights.length > 0 && (() => {
                      const pcat = categoryById[product.category_id];
                      const label = (pcat?.size_label || "ft").trim() || "ft";
                      const isShoe = pcat?.kind === "shoes";
                      const sizes = product.heights.join(", ");
                      return (
                        <span
                          className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] bg-[#F0EFEB] px-2 py-1 rounded-sm"
                          style={{ fontFamily: "IBM Plex Mono, monospace" }}
                        >
                          {isShoe ? `${label} ${sizes}` : `${sizes} ${label}`}
                        </span>
                      );
                    })()}
                    {product.fit_feedback && FIT_BADGE[product.fit_feedback] && (
                      <span
                        className="text-[10px] lowercase text-[#2D302A] bg-[#E9E3D6] px-2 py-1 rounded-sm"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                      >
                        {FIT_BADGE[product.fit_feedback]}
                      </span>
                    )}
                  </div>
                  <a
                    href={product.product_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-xs md:text-sm text-[#2D302A] hover:underline mt-1"
                  >
                    View Product →
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Bookmarks;
